import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth as useClerkAuth } from "@clerk/clerk-react";
import type { ProviderName, Skill } from "@cowork/shared";
import { PROVIDER_INFOS } from "@cowork/shared";
import { useSkills } from "../../hooks/useSkills.js";
import { ModelSwitcher } from "./ModelSwitcher.js";
import { MessageBubble, StreamingBubble, ToolCallIndicator } from "./MessageBubble.js";
import { ChatInput } from "./ChatInput.js";
import { ArtifactsPanel } from "./ArtifactsPanel.js";
import type { Artifact } from "./ArtifactsPanel.js";
import { useChatStore, getSessionSummary } from "../../store/chatStore.js";
import { useProjectStore } from "../../store/projectStore.js";
import { engineWS } from "../../lib/ws.js";
import { formatCost, formatTokens } from "../../lib/utils.js";

const PROMPT_STARTERS = [
  { label: "Summarize a document", prompt: "Please summarize the following document for me:" },
  { label: "Review my code", prompt: "Please review this code for quality, security, and best practices:\n\n```\n\n```" },
  { label: "Draft an email", prompt: "Help me draft a professional email to:" },
  { label: "Create a plan", prompt: "Create a step-by-step implementation plan for:" },
  { label: "Debug an issue", prompt: "I'm seeing this error and need help debugging it:\n\n" },
  { label: "Explain a concept", prompt: "Please explain the following concept clearly:" },
];

const AUTH_ENABLED = Boolean(import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"]);
const SIDEBAR_KEY = "cowork:sidebarOpen";

function useOptionalAuth() {
  if (AUTH_ENABLED) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useClerkAuth();
  }
  return { getToken: async () => null } as ReturnType<typeof useClerkAuth>;
}

// ── Project selector dropdown ─────────────────────────────────────────────────
function ProjectSelector({
  projects,
  activeProjectId,
  onSelect,
}: {
  projects: { id: string; title: string; color: string }[];
  activeProjectId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = projects.find((p) => p.id === activeProjectId);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-colors"
        style={{
          background: activeProjectId ? "var(--accent-muted)" : "var(--bg-tertiary)",
          color: activeProjectId ? "var(--accent)" : "var(--text-secondary)",
          border: activeProjectId ? "1px solid var(--accent-border)" : "1px solid var(--border)",
        }}
      >
        {active && (
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: active.color }} />
        )}
        {active ? active.title : "No project"}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 4l3 3 3-3" /></svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute top-full left-0 mt-1 z-20 rounded-xl overflow-hidden py-1 min-w-[160px]"
            style={{ background: "var(--bg-primary)", border: "1px solid var(--border)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
          >
            <button
              onClick={() => { onSelect(null); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs transition-colors"
              style={{ color: activeProjectId === null ? "var(--text-primary)" : "var(--text-secondary)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              No project
            </button>
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => { onSelect(p.id); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors"
                style={{ color: activeProjectId === p.id ? "var(--text-primary)" : "var(--text-secondary)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
                <span className="truncate">{p.title}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────
export function ChatPanel() {
  const { getToken } = useOptionalAuth();
  const {
    sessions,
    activeSession,
    activeSessionId,
    streaming,
    partialText,
    lastError,
    activeToolCall,
    newSession,
    sendMessage,
    cancelStream,
    regenerateLast,
    setActiveSessionId,
  } = useChatStore();

  const { projects, activeProjectId, setActiveProjectId, activeProject } = useProjectStore();

  const [provider, setProvider] = useState<ProviderName>("claude");
  const [model, setModel] = useState(
    PROVIDER_INFOS.claude.models.find((m) => m.id === "claude-sonnet-4-6")?.id ?? "claude-sonnet-4-6",
  );
  const [connected, setConnected] = useState(false);
  const skills = useSkills(connected);

  // B-01: collapsible sidebar
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    return stored === null ? true : stored === "true";
  });

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((v) => {
      const next = !v;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  }, []);

  // B-04: artifacts panel
  const [activeArtifact, setActiveArtifact] = useState<Artifact | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ⌘\ keyboard shortcut for sidebar
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggleSidebar]);

  useEffect(() => {
    const tokenProvider = AUTH_ENABLED ? () => getToken() : undefined;
    engineWS.connect(tokenProvider);
    const remove = engineWS.addHandler((msg) => {
      if (msg.type === "pong") setConnected(true);
    });
    const pingInterval = setInterval(() => {
      if (engineWS.isConnected) { setConnected(true); engineWS.send({ type: "ping" }); }
      else setConnected(false);
    }, 5000);
    return () => { remove(); clearInterval(pingInterval); engineWS.disconnect(); };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeSession?.messages, partialText, activeToolCall]);

  // Build system prompt: project instructions + optional skill content
  const buildSystemPrompt = (skillContent?: string): string | undefined => {
    const parts: string[] = [];
    if (activeProject?.instructions.trim()) parts.push(activeProject.instructions.trim());
    if (activeProject?.memory && activeProject.memory.length > 0) {
      const memLines = activeProject.memory.map((m) => `- ${m.content}`).join("\n");
      parts.push(`## Project Memory\n${memLines}`);
    }
    if (skillContent) parts.push(skillContent);
    return parts.length > 0 ? parts.join("\n\n") : undefined;
  };

  const handleSend = (text: string, skill?: Skill) =>
    sendMessage(text, provider, model, buildSystemPrompt(skill?.content));

  const handleStarterClick = (prompt: string) => {
    if (!connected) return;
    sendMessage(prompt, provider, model, buildSystemPrompt());
  };

  const messages = activeSession?.messages ?? [];
  const lastAssistantIdx = [...messages].reverse().findIndex((m) => m.role === "assistant");
  const lastAssistantAbsIdx = lastAssistantIdx >= 0 ? messages.length - 1 - lastAssistantIdx : -1;
  const summary = activeSession ? getSessionSummary(activeSession) : null;

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Session sidebar ─────────────────────────────────────────────── */}
      <div
        className="flex flex-col flex-shrink-0 overflow-hidden transition-all duration-200"
        style={{
          width: sidebarOpen ? 220 : 0,
          borderRight: sidebarOpen ? "1px solid var(--border)" : "none",
          background: "var(--bg-secondary)",
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? "auto" : "none",
        }}
      >
        <div className="p-2.5 flex-shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
          <button
            onClick={() => newSession(provider, model)}
            className="w-full flex items-center gap-2 px-2.5 py-2 text-xs rounded-lg transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; (e.currentTarget as HTMLElement).style.color = "var(--text-primary)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)"; }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <line x1="6" y1="1" x2="6" y2="11" /><line x1="1" y1="6" x2="11" y2="6" />
            </svg>
            New conversation
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1 px-1.5">
          {sessions.map((s) => {
            const sum = getSessionSummary(s);
            const isActive = s.id === activeSessionId;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSessionId(s.id)}
                className="w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors mb-0.5"
                style={{
                  background: isActive ? "var(--bg-tertiary)" : "transparent",
                  color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
                }}
                onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "var(--bg-hover)"; }}
                onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
              >
                <div className="truncate font-medium">{s.title}</div>
                <div className="flex items-center justify-between mt-0.5" style={{ color: "var(--text-tertiary)" }}>
                  <span className="text-[10px]">{s.model.split("-").slice(0, 3).join("-")}</span>
                  {sum.totalCostUsd > 0 && <span className="text-[10px]">{formatCost(sum.totalCostUsd)}</span>}
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-3 py-2.5 flex-shrink-0" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: connected ? "#4ade80" : "#f87171" }} />
            <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
              {connected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </div>
      </div>

      {/* ── Main chat area ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5 flex-shrink-0 gap-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Sidebar toggle */}
            <button
              onClick={toggleSidebar}
              title={sidebarOpen ? "Collapse sidebar (⌘\\)" : "Expand sidebar (⌘\\)"}
              className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors"
              style={{ color: "var(--text-tertiary)" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; (e.currentTarget as HTMLElement).style.color = "var(--text-secondary)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; (e.currentTarget as HTMLElement).style.color = "var(--text-tertiary)"; }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="1" y1="4" x2="13" y2="4" />
                <line x1="1" y1="7" x2="13" y2="7" />
                <line x1="1" y1="10" x2="13" y2="10" />
              </svg>
            </button>

            <h1 className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>
              {activeSession?.title ?? "New conversation"}
            </h1>
            {summary && summary.totalCostUsd > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: "var(--bg-tertiary)", color: "var(--text-tertiary)" }}>
                {formatTokens(summary.totalInputTokens + summary.totalOutputTokens)} · {formatCost(summary.totalCostUsd)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Project selector */}
            <ProjectSelector
              projects={projects as { id: string; title: string; color: string }[]}
              activeProjectId={activeProjectId}
              onSelect={setActiveProjectId}
            />

            <ModelSwitcher
              provider={provider}
              model={model}
              onProviderChange={setProvider}
              onModelChange={setModel}
              disabled={streaming}
            />
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 && !streaming ? (
            <div className="flex flex-col items-center justify-center h-full px-8 pb-16">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: "var(--accent)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8" stroke="rgba(255,255,255,0.9)" strokeWidth="2" fill="none" />
                  <circle cx="12" cy="12" r="3.5" fill="rgba(255,255,255,0.95)" />
                </svg>
              </div>
              {activeProject ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: activeProject.color }} />
                    <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{activeProject.title}</span>
                  </div>
                  <h2 className="text-xl font-semibold mb-2 text-center" style={{ color: "var(--text-primary)" }}>
                    How can I help with {activeProject.title}?
                  </h2>
                </>
              ) : (
                <h2 className="text-xl font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
                  How can I help you today?
                </h2>
              )}
              <p className="text-sm mb-8 text-center max-w-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {activeProject?.description || "Your AI work assistant. Switch between Claude, GPT-4, Gemini, and local models anytime."}
              </p>

              <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
                {PROMPT_STARTERS.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => handleStarterClick(s.prompt)}
                    disabled={!connected}
                    className="text-left px-4 py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--accent-border)"; (e.currentTarget as HTMLElement).style.background = "var(--bg-tertiary)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "var(--border)"; (e.currentTarget as HTMLElement).style.background = "var(--bg-secondary)"; }}
                  >
                    <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-4">
              {messages.map((msg, i) => {
                const canRegenerate = i === lastAssistantAbsIdx && msg.role === "assistant" && !streaming;
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isLast={i === lastAssistantAbsIdx}
                    onOpenArtifact={(code, language, title) => setActiveArtifact({ code, language, title })}
                    {...(canRegenerate && { onRegenerate: () => regenerateLast(provider, model) })}
                  />
                );
              })}

              {activeToolCall && <ToolCallIndicator toolName={activeToolCall} />}
              {streaming && partialText && <StreamingBubble text={partialText} />}

              {streaming && !partialText && !activeToolCall && (
                <div className="flex gap-4 px-4 py-3">
                  <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center animate-pulse" style={{ background: "var(--accent)" }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="5" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" fill="none" />
                      <circle cx="7" cy="7" r="2" fill="rgba(255,255,255,0.95)" />
                    </svg>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:0ms]" style={{ background: "var(--text-tertiary)" }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:150ms]" style={{ background: "var(--text-tertiary)" }} />
                    <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:300ms]" style={{ background: "var(--text-tertiary)" }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {lastError && (
          <div className="mx-5 mb-2 px-4 py-2.5 rounded-xl text-xs flex items-start gap-2" style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", color: "#f87171" }}>
            <span className="flex-shrink-0">⚠</span>
            <span className="break-all">{lastError}</span>
          </div>
        )}

        <ChatInput
          onSend={handleSend}
          skills={skills}
          disabled={!connected}
          onCancel={cancelStream}
          streaming={streaming}
        />
      </div>

      {/* ── Artifacts panel ─────────────────────────────────────────────── */}
      {activeArtifact && (
        <ArtifactsPanel
          artifact={activeArtifact}
          onClose={() => setActiveArtifact(null)}
        />
      )}
    </div>
  );
}
